import axios from 'axios';
import { auth } from './firebase';

// Create axios instance with base configuration
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    async (config) => {
        const user = auth.currentUser;
        if (user) {
            const token = await user.getIdToken();
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            console.error('Unauthorized access - please login again');
        }
        return Promise.reject(error);
    }
);

export default api;

// API Methods

// Auth
export const verifyToken = () => api.post('/auth/verify');
export const getCurrentUser = () => api.get('/auth/user');

// Menu
export const getMenuItems = (params?: any) => api.get('/menu', { params });
export const getMenuItem = (id: string) => api.get(`/menu/${id}`);
export const getCategories = () => api.get('/menu/categories');
export const getItemsByCategory = (category: string) => api.get(`/menu/category/${category}`);

// Cart
export const getCart = () => api.get('/cart');
export const addToCart = (itemId: string, quantity: number) =>
    api.post('/cart/add', { itemId, quantity });
export const updateCartItem = (itemId: string, quantity: number) =>
    api.put(`/cart/update/${itemId}`, { quantity });
export const removeFromCart = (itemId: string) => api.delete(`/cart/remove/${itemId}`);
export const clearCart = () => api.delete('/cart/clear');

// Orders
export const createOrder = (data: any) => api.post('/orders', data);
export const getOrders = () => api.get('/orders');
export const getOrder = (id: string) => api.get(`/orders/${id}`);
export const cancelOrder = (id: string) => api.put(`/orders/${id}/cancel`);
export const trackOrder = (id: string) => api.get(`/orders/${id}/track`);

// Admin - Orders
export const getAllOrders = () => api.get('/admin/orders');
export const updateOrderStatus = (id: string, status: string) =>
    api.put(`/admin/orders/${id}/status`, { status });
export const verifyUserPhone = (id: string) => api.put(`/admin/orders/${id}/verify-phone`);

// User
export const getUserProfile = () => api.get('/user/profile');
export const updateUserProfile = (data: any) => api.put('/user/profile', data);
export const addAddress = (data: any) => api.post('/user/address', data);
export const getAddresses = () => api.get('/user/addresses');
export const updateAddress = (id: string, data: any) => api.put(`/user/address/${id}`, data);
export const deleteAddress = (id: string) => api.delete(`/user/address/${id}`);

// Admin - Menu Management
export const createMenuItem = (data: any) => api.post('/admin/menu', data);
export const updateMenuItem = (id: string, data: any) => api.put(`/admin/menu/${id}`, data);
export const deleteMenuItem = (id: string) => api.delete(`/admin/menu/${id}`);
export const updateMenuItemAvailability = (id: string, isAvailable: boolean) =>
    api.patch(`/admin/menu/${id}/availability`, { isAvailable });

// Admin - Table Management
export const clearTable = (tableNumber: string) => api.post(`/admin/tables/${tableNumber}/clear`);
export const getActiveTableOrders = (tableNumber: string) => api.get(`/admin/tables/${tableNumber}/orders`);
